import { createAppButton, createAppFlyoutAnchor, createAppMenuItem } from 'owa-command-ribbon';
import type { ReadOnlyRibbonControlDefProps } from 'owa-mail-ribbon-utils';
import type {
    AppButtonOnExecuteParameter,
    RibbonControlProps,
    MenuItemControlProps,
    MenuSection,
} from 'owa-acui';
import type { MailboxInfo } from 'owa-client-types';
import { owaComputedFn } from 'owa-computed-fn';
import { getComputedCallback } from 'owa-ribbon-mobx-utils/lib/getComputedCallback';
import type { IButtonStyles, IContextualMenuItem, IContextualMenuProps } from '@fluentui/react';
import { ContextualMenuItemType } from '@fluentui/react';
import { getAddinIcon } from 'owa-addins-surface-actions/lib/utils/getAddinIcon';
import type { AddinCommandSurfaceItem, AddinLocation } from 'owa-addins-types';
import type { RuntimeControlRibbonCapabilites } from 'owa-m365-acquisitions/lib/utils/runtimeControlRibbonCapabilites';
import { getIsReadButtonDisabled } from 'owa-mail-ribbon-utils/lib/getIsReadButtonDisabled';
import { getReadRibbonId } from 'owa-mail-ribbon-utils/lib/getReadRibbonId';
import { getRibbonMailboxInfo } from 'owa-mail-ribbon-utils/lib/getRibbonMailboxInfo';
import {
    isMultipleSelection,
    isNoItemContext,
} from 'owa-message-extension-flyout/lib/utils/messageExtensionItemContext';
import { onPinnedMessageExtensionExecute } from 'owa-mail-ribbon-command-actions/lib/onPinnedMessageExtensionExecute';
import { isOffline } from 'owa-offline';
import {
    getMultiSelectHostItemIndex,
    getReadHostItemIndex,
} from 'owa-addins-store/lib/utils/hostItemIndexUtils';
import { lazyShowOfflineModeDialog } from 'owa-addins-view';
import { getSingleSelectedItemFromView } from 'owa-mail-ribbon-command-actions/lib/getSelectedItemFromView';
import { getDefaultRibbonStyles } from 'owa-command-ribbon-styles/lib/util/getDefaultRibbonStyles';
import getSelectedTableView from 'owa-mail-list-store/lib/utils/getSelectedTableView';
import { getAddinSubMenuProps } from 'owa-addins-store/lib/selectors/getAddinSurface';
import { getIsDisabledForDecryption } from '../../utils/getIsDisabledForDecryption';

/**
 * Create a button for an add-in in the read ribbon. Spam add-in
 *
 * @export
 * @param {AddinCommandSurfaceItem} addIn
 * @param {number} controlId
 * @param {Window} targetWindow
 * @param {IButtonStyles} styles
 * @param {ReadOnlyRibbonControlDefProps} props
 * @returns {RibbonControlProps}
 */
export const createAddinButtonForReadInRibbon: (
    addIn: AddinCommandSurfaceItem,
    controlId: number,
    targetWindow: Window,
    styles: IButtonStyles,
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function createAddinButtonForReadInRibbon(
    addIn: AddinCommandSurfaceItem,
    controlId: number,
    targetWindow: Window,
    styles: IButtonStyles,
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const { isPopout, itemId } = props;
    const readRibbonId = getReadRibbonId(controlId, itemId);
    const params = {} as AppButtonOnExecuteParameter;
    params.id = readRibbonId as string;
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const isDisabledForDecryption = getIsDisabledForDecryption(itemId, mailboxInfo);

    return createAppButton({
        id: readRibbonId,
        label: addIn?.title ?? addIn?.name ?? '',
        onExecute: getComputedCallback(
            controlId,
            onPinnedMessageExtensionExecute,
            addIn.key,
            targetWindow,
            'MessageRead' /* entrypoint */,
            params,
            itemId?.Id,
            '' /* composeId */,
            mailboxInfo
        ),
        icon: getAddinIcon(addIn) ?? '',
        styles,
        mailboxInfo,
        overrides: {
            disabled:
                getIsReadButtonDisabled(controlId, isPopout, mailboxInfo, itemId) ||
                isDisabledForDecryption,
            customTooltip: addIn.ariaLabel,
            ariaDescription: addIn.ariaLabel,
        },
    });
});

export const getReadRibbonRuntimeControlProps: (
    addIn: AddinCommandSurfaceItem,
    controlId: number,
    targetWindow: Window,
    styles: IButtonStyles,
    props: ReadOnlyRibbonControlDefProps,
    ribbonContext: RuntimeControlRibbonCapabilites
) => RibbonControlProps = owaComputedFn(function getReadRibbonRuntimeControlProps(
    addIn: AddinCommandSurfaceItem,
    controlId: number,
    targetWindow: Window,
    styles: IButtonStyles,
    props: ReadOnlyRibbonControlDefProps,
    ribbonContext: RuntimeControlRibbonCapabilites
): RibbonControlProps {
    const { isPopout, itemId } = props;
    const readRibbonId = getReadRibbonId(controlId, itemId);
    const ribbonMailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const isDisabledForDecryption = getIsDisabledForDecryption(itemId, ribbonMailboxInfo);

    const isMultiSelect = isMultipleSelection(props.isPopout);
    const isNoContext = isNoItemContext();
    const messageItemId = isMultiSelect || isNoContext ? '' : itemId?.Id ?? undefined;

    const isAddinDisabledForNoItemContext = isNoContext && !ribbonContext.isEnabledInNoContext;
    const isAddinDisabledForMultipleSelection =
        isMultiSelect && !ribbonContext.isEnabledInMultiSelect;

    const entryPoint = isNoContext
        ? 'NoItemContext'
        : isMultiSelect
        ? 'MultipleSelection'
        : 'MessageRead';
    const cacheKey = `${addIn.key.toLowerCase()}_${
        isMultiSelect ? 'MultipleSelection' : 'MessageRead'
    }`;
    const addinSubMenuProps: IContextualMenuProps | undefined = getAddinSubMenuProps(cacheKey);

    if (addinSubMenuProps) {
        const hostItemIndex =
            isNoContext || isMultiSelect
                ? getMultiSelectHostItemIndex('0') // to be consistent with getAddInLaunchContext
                : getReadHostItemIndex(itemId?.Id ? itemId.Id : '');
        const addinLocation = {
            hostItemIndex,
            targetWindow,
        };
        const menuItems: MenuSection[] = getAddinsSection(
            addIn,
            readRibbonId as string,
            addinLocation,
            entryPoint,
            isPopout,
            addinSubMenuProps,
            ribbonMailboxInfo,
            itemId?.Id
        );
        if (menuItems.length > 0) {
            return createAppFlyoutAnchor(
                readRibbonId /* controlId */,
                addIn?.title ?? addIn?.name ?? '' /* label */,
                { sections: menuItems } /* menuDefinition */,
                ribbonMailboxInfo /* mailboxInfo */,
                undefined /* keyTip */,
                getAddinIcon(addIn) ?? '' /* icon */,
                undefined /* iconColor */,
                styles,
                {
                    disabled:
                        isOffline() ||
                        getIsReadButtonDisabled(controlId, isPopout, ribbonMailboxInfo, itemId) ||
                        isAddinDisabledForMultipleSelection ||
                        isAddinDisabledForNoItemContext ||
                        isDisabledForDecryption,
                    customTooltip: addIn.ariaLabel,
                    onExecute: async (_params?: AppButtonOnExecuteParameter) => {
                        if (isOffline()) {
                            await lazyShowOfflineModeDialog.importAndExecute(targetWindow);
                        }
                    },
                } /* overrides */
            );
        }
    }
    return createAppButton({
        id: readRibbonId,
        label: addIn?.title ?? addIn?.name ?? '',
        onExecute: (params?: AppButtonOnExecuteParameter) => {
            if (params) {
                getComputedCallback(
                    controlId,
                    onPinnedMessageExtensionExecute,
                    addIn.key,
                    targetWindow,
                    entryPoint,
                    params,
                    messageItemId,
                    '' /* composeId */,
                    ribbonMailboxInfo
                )();
            }
        },
        icon: getAddinIcon(addIn) ?? '',
        styles,
        mailboxInfo: ribbonMailboxInfo,
        overrides: {
            disabled:
                isOffline() ||
                getIsReadButtonDisabled(controlId, isPopout, ribbonMailboxInfo, itemId) ||
                isAddinDisabledForMultipleSelection ||
                isAddinDisabledForNoItemContext ||
                isDisabledForDecryption,
            customTooltip: addIn.ariaLabel,
            ariaDescription: addIn.ariaLabel,
        },
    });
});

/**
 * Formats the add-in commands into menu sections for use in a flyout anchor
 * @param addin The add-in command surface item
 * @param uniqueIdBase A unique identifier base to ensure menu item IDs are unique
 * @param addinLocation The location context for the add-in
 * @param entryPoint The entry point context (e.g., 'MessageRead', 'MessageCompose')
 * @param isPopout Whether the current window is a popout
 * @param addinSubMenuProps The submenu properties for the add-in
 * @param mailboxInfo The mailbox information context for telemetry purposes
 * @param itemId Optional item ID for context
 * @returns An array of MenuSection objects representing the add-in commands
 */
export const getAddinsSection: (
    addin: AddinCommandSurfaceItem,
    uniqueIdBase: string,
    addinLocation: AddinLocation,
    entryPoint: string,
    isPopout: boolean,
    addinSubMenuProps: IContextualMenuProps | undefined,
    mailboxInfo: MailboxInfo,
    itemId: string | undefined
) => MenuSection[] = owaComputedFn(
    (
        addin: AddinCommandSurfaceItem,
        uniqueIdBase: string,
        addinLocation: AddinLocation,
        entryPoint: string,
        isPopout: boolean,
        addinSubMenuProps: IContextualMenuProps | undefined,
        mailboxInfo: MailboxInfo,
        itemId: string | undefined
    ): MenuSection[] => {
        const menuItems: MenuItemControlProps[] = [];
        let items: IContextualMenuItem[] | undefined = [];

        if (addin.sectionProps?.items) {
            items = addin.sectionProps.items;
        } else if (addinSubMenuProps?.items) {
            items = addinSubMenuProps.items[0].sectionProps?.items;
        }

        items?.forEach((menuItem: IContextualMenuItem) => {
            if (
                menuItem.itemType != ContextualMenuItemType.Divider &&
                menuItem.id &&
                menuItem.name
            ) {
                const localUniqueId = menuItem.id + addin.id;

                if (menuItem.subMenuProps) {
                    const subMenuItems: MenuSection[] = getAddinsSection(
                        menuItem,
                        uniqueIdBase,
                        addinLocation,
                        entryPoint,
                        isPopout,
                        menuItem.subMenuProps,
                        mailboxInfo,
                        itemId
                    );

                    if (subMenuItems.length > 0) {
                        const flyoutId = localUniqueId + uniqueIdBase;
                        menuItems.push(
                            createAppFlyoutAnchor(
                                flyoutId,
                                menuItem.text ?? menuItem.name ?? '' /* label */,
                                {
                                    sections: subMenuItems,
                                },
                                mailboxInfo,
                                undefined /* keytipProps */,
                                getAddinIcon(menuItem, true /* isMenuItem*/) ?? '' /* icon */,
                                undefined /* iconColor */,
                                getDefaultRibbonStyles(),
                                {
                                    customTooltip: menuItem.title,
                                }
                            )
                        );
                    }
                } else {
                    const uniqueId = localUniqueId + uniqueIdBase;
                    menuItems.push(
                        createAppMenuItem(
                            uniqueId,
                            menuItem.text ?? menuItem.name ?? '' /* label */,
                            () => {
                                const isNoContext = isNoItemContext();
                                if (entryPoint == 'MultipleSelection' || isNoContext) {
                                    addinLocation.hostItemIndex = getMultiSelectHostItemIndex('0');
                                } else {
                                    addinLocation.hostItemIndex = getReadHostItemIndex(
                                        isPopout
                                            ? itemId ?? ''
                                            : getSingleSelectedItemFromView(getSelectedTableView())
                                                  ?.ItemId?.Id ?? ''
                                    );
                                }
                                menuItem.getClickHandler(addinLocation)();
                            },
                            mailboxInfo,
                            getAddinIcon(menuItem, true /* isMenuItem*/) ?? '' /* icon */,
                            undefined /* iconColor */,
                            getDefaultRibbonStyles(),
                            {
                                customTooltip: menuItem.title,
                            }
                        )
                    );
                }
            }
        });

        return [{ controls: menuItems }];
    }
);
